import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle2, Circle, ChevronRight, CalendarDays, Lock } from 'lucide-react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';

const META = {
  training: { color: '#00C8FF', label: 'Training Program' },
  nutrition: { color: '#65A30D', label: 'Nutrition Program' },
  recovery: { color: '#A78BFA', label: 'Recovery Program' },
};

export function ProgramHeader({ type }) {
  const [prog, setProg] = useState(null);
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    api.get('/my-programs').then(r => setProg((r.data || []).find(p => p.program_type === type) || null)).catch(() => {});
  }, [type]);

  const openSchedule = () => {
    setOpen(true);
    setSchedule(null);
    api.get(`/program-assignments/${prog.id}/schedule`).then(r => setSchedule(r.data)).catch(() => {});
  };

  if (!prog) return null;
  const m = META[type];
  let currentWeek = null;
  const notStarted = prog.start_date && prog.start_date > format(new Date(), 'yyyy-MM-dd');
  try {
    if (prog.start_date && prog.duration_weeks && !notStarted) {
      const days = differenceInCalendarDays(new Date(), parseISO(prog.start_date));
      currentWeek = Math.max(1, Math.min(prog.duration_weeks, Math.floor(days / 7) + 1));
    }
  } catch { /* noop */ }
  return (
    <>
      <button onClick={openSchedule} data-testid={`program-header-${type}`} className="w-full text-left regen-card-elevated p-5 relative overflow-hidden active:scale-[0.99] transition" style={{ borderColor: `${m.color}33` }}>
        <div className="absolute inset-y-0 left-0 w-1" style={{ background: m.color }} />
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="label-mini" style={{ color: m.color }}>{m.label}</div>
            <div className="font-display font-black text-xl mt-0.5 truncate">{prog.name}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
              {notStarted && <span className="text-[11px] font-mono font-semibold" style={{ color: m.color }} data-testid={`program-starts-${type}`}>Starts {format(parseISO(prog.start_date), 'EEE, d MMM')}</span>}
              {currentWeek && <span className="text-[11px] font-mono" style={{ color: m.color }} data-testid={`program-week-${type}`}>Week {currentWeek} of {prog.duration_weeks}</span>}
              {prog.goal ? <span className="text-xs text-muted-foreground">· Goal: {prog.goal}</span> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] mt-3" style={{ color: m.color }}>
          <CalendarDays className="w-3.5 h-3.5" /> View full program <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-2xl lg:max-w-5xl max-h-[88vh] overflow-y-auto" data-testid={`program-schedule-${type}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: m.color }} />{prog.name}</DialogTitle>
          </DialogHeader>
          {!schedule && <div className="py-10 text-center text-sm text-muted-foreground">Loading program…</div>}
          {schedule && (
            <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start">
              <div className="text-xs text-muted-foreground lg:col-span-2">{schedule.duration_weeks} weeks · starts {schedule.start_date}</div>
              {schedule.weeks.length === 0 && <div className="text-sm text-muted-foreground lg:col-span-2">No scheduled sessions.</div>}
              {schedule.weeks.map(wk => {
                const wkLocked = prog.locked_from_week && wk.week_number >= prog.locked_from_week;
                return (
                <div key={wk.week_number} className="rounded-xl border border-[var(--c-hair)] overflow-hidden">
                  <div className="px-4 py-2 bg-[var(--c-film)] font-display font-bold text-sm flex items-center gap-2" style={{ color: m.color }}>
                    Week {wk.week_number}{wkLocked && <Lock className="w-3.5 h-3.5" />}
                  </div>
                  {wkLocked ? (
                    <div className="px-4 py-4 text-xs text-muted-foreground" data-testid={`week-locked-${wk.week_number}`}>
                      Locked during your free trial. Subscribe to YourReGen+ to unlock your full 4-week program.
                    </div>
                  ) : (
                  <div className="divide-y divide-[var(--c-hair)]">
                    {wk.days.map((day, i) => (
                      <div key={i} className="px-4 py-2.5 flex gap-3">
                        <div className="w-12 shrink-0">
                          <div className="text-xs font-bold uppercase text-muted-foreground">{day.date ? format(parseISO(day.date), 'EEE') : `Day ${day.day_index + 1}`}</div>
                          <div className="text-[10px] text-muted-foreground/60">{(day.date || '').slice(5)}</div>
                        </div>
                        <div className="flex-1 space-y-1">
                          {day.items.map((it, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm">
                              {it.done ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: m.color }} /> : <Circle className="w-4 h-4 shrink-0 text-muted-foreground/50" />}
                              <span className={it.done ? 'text-muted-foreground line-through' : ''}>{it.name}</span>{it.extra && <span className="ml-1.5 text-[10px] text-[#00C8FF]" data-testid="schedule-extra-tag">Extra</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
